// src/api/draftingAssistantApi.ts

import { ChatStreamCallbacks, ChatResponseChunk } from "../interfaces/chatTypes";
import { ChatApiError } from "./chatApi";
import { handleApiErrorResponse } from "./utils/errorHandler";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { normalizeAccessToken } from "../utils/typeHelpers";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { routeChunkToCallback, ChunkRouterState } from "./utils/chunkRouter";
import { streamErrorToPostHog, ApiNames } from "./utils/captureError";
const log = createLogger("DraftingAssistantApi");

// Base URL for the API
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Drafting Assistant API Request Interface
 *
 * This interface matches the document generation API specification
 * with support for multiple additional context files
 */
interface DraftingAssistantRequest {
  message: string;
  full_text_file: File;
  requirements?: File; // Currently not used, but kept for future
  additional_context?: File[]; // Multiple context files
  selected_text?: File;
  id?: string;
  bypass_cache?: boolean;
  details?: any;
  chat_history?: any[];
  access_token?: string; // Optional access token for authentication
}

/**
 * Drafting Assistant API Client
 *
 * Handles communication with the document generation endpoint
 * for the drafting assistant agent
 */
class DraftingAssistantApi {
  private abortController: AbortController | null = null;
  private routerState: ChunkRouterState = {};

  /**
   * Send a streaming chat request to the document generation API
   *
   * @param request The drafting assistant request with document and context files
   * @param callbacks Callbacks for handling streaming response
   */
  async sendStreamingChatRequest(
    request: DraftingAssistantRequest,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Log the request details to debug web search
    log.info("🔍 DRAFTING ASSISTANT API REQUEST:", {
      hasDetails: !!request.details,
      webSearchFlag: request.details?.web_search,
      details: request.details,
    });

    log.info("Sending drafting assistant chat request", {
      hasMainDocument: !!request.full_text_file,
      contextFileCount: request.additional_context?.length || 0,
      hasSelectedText: !!request.selected_text,
      messagePreview: request.message.substring(0, 50) + "...",
    });

    // Create form data for multipart request
    const formData = new FormData();
    formData.append("message", request.message);
    formData.append("full_text_file", request.full_text_file);

    // Add multiple additional context files
    if (request.additional_context && request.additional_context.length > 0) {
      log.info("📎 ADDING CONTEXT FILES:", {
        count: request.additional_context.length,
        files: request.additional_context.map((f, i) => ({
          index: i + 1,
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      });

      request.additional_context.forEach((file, index) => {
        formData.append("additional_context", file);
        log.debug(`Added context file ${index + 1}:`, {
          fileName: file.name,
          fileSize: file.size,
        });
      });
    }

    // Add optional fields
    if (request.selected_text) {
      formData.append("selected_text", request.selected_text);
    }

    if (request.id) {
      formData.append("id", request.id);
    }

    if (request.bypass_cache !== undefined) {
      formData.append("bypass_cache", request.bypass_cache.toString());
    }

    if (request.details) {
      // Log the details being sent for debugging the new features
      log.debug("Sending enhanced details configuration", {
        web_search: request.details.web_search,
        thinking: request.details.thinking,
        benchmark_mode: request.details.benchmark_mode,
      });

      formData.append("details", JSON.stringify(request.details));
    }

    if (request.chat_history) {
      formData.append("chat_history", JSON.stringify(request.chat_history));
    }

    // Create abort controller for cancellation
    this.abortController = new AbortController();
    // Reset router state for new streaming session
    this.routerState = {};

    const url = `${API_BASE_URL}/chat/document_generator/stream/`;

    log.info("📤 DRAFTING ASSISTANT API CALL:", {
      url,
      hasMainDocument: !!request.full_text_file,
      contextFileCount: request.additional_context?.length || 0,
      contextFiles: request.additional_context?.map((f) => ({ name: f.name, size: f.size })) || [],
      hasSelectedText: !!request.selected_text,
      sessionId: request.id,
      accessToken: request.access_token,
    });

    try {
      callbacks.onStart();

      const api_request = constructRequest(
        url,
        "POST",
        normalizeAccessToken(request.access_token),
        formData,
        this.abortController.signal
      );

      const response = await fetch(api_request);

      if (!response.ok) {
        log.error("Drafting assistant API returned error response", {
          status: response.status,
          statusText: response.statusText,
        });

        // Use shared utility to parse and handle error response
        const apiError = await handleApiErrorResponse(response);
        streamErrorToPostHog(apiError, ApiNames.DRAFTING_ASSISTANT_API);
        callbacks.onError(apiError);
        return;
      }

      // Process streaming response
      await this.processStreamingResponse(response, callbacks);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        log.info("Drafting assistant request cancelled");
        callbacks.onComplete();
        return;
      }

      log.error("Drafting assistant chat request failed", { error });

      if (error instanceof ChatApiError) {
        streamErrorToPostHog(error, ApiNames.DRAFTING_ASSISTANT_API, undefined, {
          chat_id: request.id,
        });
        callbacks.onError(error);
      } else {
        // Handle client-side errors with helpful, contextual messages
        const clientError = createClientErrorInfo(error, "streaming");
        // Also capture the ORIGINAL error: clientError only carries the
        // user-facing message and the CONNECTION_FAILED code, so
        // without this we never see the underlying message (e.g. "Failed to
        // fetch"). chat_id joins this to the BE "Stream outcome" log.
        streamErrorToPostHog(clientError, ApiNames.DRAFTING_ASSISTANT_API, undefined, {
          chat_id: request.id,
          original_error_name: error instanceof Error ? error.name : undefined,
          original_error_message: error instanceof Error ? error.message : String(error),
        });
        callbacks.onError(new ChatApiError(clientError.message, clientError.errorCode, 0));
      }
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Process streaming response from the API
   */
  private async processStreamingResponse(
    response: Response,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim().startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              callbacks.onComplete();
              return;
            }

            try {
              const parsed: ChatResponseChunk = JSON.parse(data);
              this.handleChunk(parsed, callbacks);
            } catch (e) {
              log.warn("Failed to parse streaming chunk", { data, error: e });
            }
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        if (buffer.trim().startsWith("data: ")) {
          const data = buffer.slice(6).trim();
          if (data !== "[DONE]") {
            try {
              const parsed: ChatResponseChunk = JSON.parse(data);
              this.handleChunk(parsed, callbacks);
            } catch (e) {
              log.warn("Failed to parse final chunk", { data, error: e });
            }
          }
        }
      }

      callbacks.onComplete();
    } catch (error) {
      throw error;
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Handle individual streaming chunks
   */
  private handleChunk(chunk: ChatResponseChunk, callbacks: ChatStreamCallbacks): void {
    // Use shared chunk router with standard configuration and persistent state
    routeChunkToCallback(chunk, callbacks, log, {
      verboseAutoRedlineLogging: true,
      verboseWebSearchLogging: true,
      enhancedErrorHandling: true,
      enableFallbackToText: true,
      state: this.routerState,
    });
  }

  /**
   * Cancel any ongoing request
   */
  cancelRequest(): void {
    if (this.abortController) {
      log.info("Cancelling drafting assistant request");
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// Export singleton instance
export const draftingAssistantApi = new DraftingAssistantApi();
